import React, { FC, useEffect, useState } from "react";
import { Container, Dimmer, Input, Loader, Message } from "semantic-ui-react";

import { api, useApi } from "../../../api/api";
import { IOApiTableActions } from "../../../components/IOApiTable";
import { envColor } from "../../../common";
import VenueConfigsIOApiTable from "./VenueConfigsIOApiTable";
import VenueConfigsDirectoryTree from "./VenueConfigsDirectoryTree";
import { SyncButton } from "../../../components/buttons";
import Uploader from "../../../components/Uploader";

interface Config {
    srckey: string;
    author: string;
    created_at: string;
    source: string;
    version: string;
    searchingValue: string;
}

export interface DirectoryItem {
    name: string;
    items: DirectoryItem[];
    config?: Config;
}

const VenueConfigs: FC<{}> = () => {
    const [actions, setActions] = useState<IOApiTableActions>();
    const [loading, setLoading] = useState(false);
    const [configs, setConfigs] = useState<Config[]>([]);
    const [searchValue, setSearchValue] = useState<string>();
    const [searchResults, setSearchResults] = useState<Config[]>([]);

    const configsApi = useApi(api.configs);

    const load = async (path: string) => {
        return configsApi.index().catch((e) => console.error(e));
    };

    const index = async (path: string = "") => {
        setLoading(true);
        const content: Config[] = await load(path);
        const contentWithSearchField = content.map((item) => {
            const concatenatedValue = Object.values(item)
                .filter((value) => value !== null)
                .join("")
                .toLowerCase();
            return { ...item, searchingValue: concatenatedValue };
        });

        setConfigs(contentWithSearchField);


        if (searchValue) {
            const filteredResults = contentWithSearchField.filter((config) => config.searchingValue.includes(searchValue.toLowerCase()));
            setSearchResults(filteredResults);
        }

        setLoading(false);
        return content;
    };

    const upload = async (files: File[]) => {
        const hasSQSUpdate = files.find(f => f.name === "sqscfg.json");
        if (hasSQSUpdate) {
            configsApi.request_write(files);
            return;
        }

        setLoading(true);
        await configsApi.write(files);
        await index();
    };

    const remove = async (record: Record<string, any>) => {
        setLoading(true);
        await configsApi.remove(record);
        await index();
    };

    const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchValue(value);
        if (!value) {
            setSearchResults([]);
            return;
        }

        const filteredResults = configs.filter((config) => config.searchingValue.includes(value.toLowerCase()));
        setSearchResults(filteredResults);
    };

    useEffect(() => {
        const fetchData = async () => {
            await index();
        };
        fetchData().catch(console.error);
    }, []);

    const renderTable = () => (
        <div key={searchValue}>
            {searchValue ? (
                searchResults.length > 0 ? (
                    <Container>
                        <VenueConfigsIOApiTable
                            actions={actions}
                            onInit={setActions}
                            envColor={envColor}
                            items={searchResults}
                            isFiltered={true}
                            searchValue={searchValue}
                            remove={remove}
                        />
                    </Container>
                ) : (
                    <Message info style={{ marginTop: "10px" }}>
                        <p>Your search did not match any venue configs.</p>
                    </Message>
                )
            ) : (
                <VenueConfigsDirectoryTree
                    data={convertData(configs)}
                    actions={actions}
                    onInit={setActions}
                    remove={remove}
                />
            )}
        </div>
    );

    function convertData(configs: Config[]): DirectoryItem[] {
        function buildTree(path: string[], config: Config, output: DirectoryItem): void {
            let newChild: DirectoryItem;
            if (path.length === 0) {
                return;
            }
            const [head, ...tail] = path;
            const child = output.items.find((item) => item.name === head);
            if (!child) {
                if (path.length === 1) {
                    newChild = { name: head, items: [], config: config };
                } else {
                    newChild = { name: head, items: [] };
                }
                output.items.push(newChild);
                buildTree(tail, config, newChild);
            } else {
                buildTree(tail, config, child);
            }
        }

        const outputData: DirectoryItem = { name: "", items: [] };
        for (const config of configs) {
            const srckeyParts: string[] = config.srckey.split("/");
            buildTree(srckeyParts, config, outputData);
        }

        if (outputData.items.length > 0) {
            return outputData.items[0].items;
        }
        return outputData.items;
    }

    return (
        <Container>
            <Container style={{ display: "flex" }}>
                <SyncButton load={index} color={envColor} />
                <Uploader onUpload={upload} />
                <Input
                    icon="search"
                    placeholder="search"
                    style={{ flex: "1", marginLeft: "13px" }}
                    iconPosition="left"
                    onChange={handleSearchInputChange}
                    className={"search"}
                />
            </Container>

            {loading ? (
                <Dimmer inverted active={true}>
                    <Loader />
                </Dimmer>
            ) : (
                renderTable()
            )}
        </Container>
    );
};

export default VenueConfigs;
